<template>
  <div class="uploadImg-item">
    <div style="display: flex; flex-wrap: wrap;">
      <div
        class="ant-upload-picture-card-wrapper img-card"
        v-for="(url, index) in imageUrlList"
        :key="index"
      >
        <div class="img-wrapper">
          <img class="img" :src="url" alt="preview" />
          <div class="hover-img">
            <EyeOutlined style="color: #FFF;" @click.stop="openView(url)" />
            &nbsp;&nbsp;
            <DeleteOutlined
              v-if="!disabled"
              style="color: #FFF;"
              @click.stop="delImg(index)"
            />
          </div>
        </div>
      </div>

      <a-upload
        v-if="imageUrlList.length < limit && !disabled"
        accept="image/*"
        list-type="picture-card"
        class="avatar-uploader"
        :show-upload-list="false"
        :maxCount="1"
        :before-upload="beforeUpload"
        :customRequest="customRequest"
      >
        <div>
          <loading-outlined v-if="loading"></loading-outlined>
          <plus-outlined v-else></plus-outlined>
          <div class="ant-upload-text" style="margin-top: 8px">上传图片</div>
        </div>
      </a-upload>
    </div>

    <div style="width: 100%; margin-top: 10px;" v-if="!disabled">
      <MaterialSelectionListModal
        style="display: block;"
        @submit="handleMaterialSubmit"
        :validateImageOrVideoArea="validateArea"
        :validateImageOrVideoFormatAndSizeByMB="
          validateImageOrVideoFormatAndSizeByMB
        "
        :fileterResourceType="1"
        :siteId="Number(siteId)"
        :terminalId="Number(terminalId)"
        :subTerminalId="Number(subTerminalId)"
      />
    </div>
  </div>
  <a-modal :visible="previewVisible" :footer="null" @cancel="handleCancel">
    <img alt="preview" style="width: 100%" :src="previewImage" />
  </a-modal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { message, Upload } from 'ant-design-vue'
import {
  PlusOutlined,
  LoadingOutlined,
  EyeOutlined,
  DeleteOutlined
} from '@ant-design/icons-vue'
import { formRequest } from 'cupshe-axios'

const props = defineProps({
  siteId: {
    type: [String, Number],
    default: ''
  },
  terminalId: {
    type: [String, Number],
    default: ''
  },
  subTerminalId: {
    type: [String, Number],
    default: ''
  },
  fileList: {
    type: [String, Array],
    default: ''
  },
  valueType: {
    type: String,
    default: 'string'
  },
  limit: {
    type: Number,
    default: 1
  },
  disabled: {
    type: Boolean,
    default: false
  },
  maxSizeMB: {
    type: Number,
    default: 2
  },
  validateArea: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'update:fileList',
  'update:materialId',
  'update:materialSource'
])

const previewVisible = ref(false)
const previewImage = ref('')
const loading = ref(false)

const imageUrlList = computed(() => {
  const url = props.fileList
  if (!url) return []
  if (props.valueType === 'arrayObject') {
    return Array.isArray(url) ? url.map(item => item.url || item) : []
  } else {
    return typeof url === 'string' ? url.split(',').filter(Boolean) : []
  }
})

const emitChange = list => {
  let res
  if (props.valueType === 'arrayObject') {
    const originalArray = Array.isArray(props.fileList) ? props.fileList : []
    res = list.map(url => {
      const existing = originalArray.find(item => (item.url || item) === url)
      return existing ? existing : { url }
    })
  } else {
    res = list.join(',')
  }
  emit('update:fileList', res)
}

const validateImageOrVideoFormatAndSizeByMB = {
  jpg: 2,
  png: 2,
  gif: 2,
  jpeg: 2,
  'image/jpeg': 2,
  'image/png': 2,
  'image/gif': 2,
  'image/jpg': 2
}

const openView = url => {
  previewImage.value = url
  previewVisible.value = true
}

const delImg = index => {
  const currentList = [...imageUrlList.value]
  currentList.splice(index, 1)
  emitChange(currentList)
  if (currentList.length === 0) {
    emit('update:materialId', '')
    emit('update:materialSource', '')
  }
}

const handleCancel = () => {
  previewVisible.value = false
}

const beforeUpload = file => {
  return new Promise((resolve, reject) => {
    const isLtSize = file.size / 1024 / 1024 <= props.maxSizeMB
    if (!isLtSize) {
      message.error(`图片大小不能超过${props.maxSizeMB}MB`)
      return reject(Upload.LIST_IGNORE)
    }

    if (props.validateArea && props.validateArea.length > 0) {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const image = new Image()
        image.src = reader.result
        image.onload = () => {
          const w = image.width
          const h = image.height

          let isValid = false
          for (let area of props.validateArea) {
            const [targetW, targetH] = area.split('*')
            if (w / h === Number(targetW) / Number(targetH)) {
              isValid = true
              break
            }
          }

          if (isValid) {
            resolve(true)
          } else {
            message.warning(
              `图片尺寸不正确，支持的比例：${props.validateArea.join(' 或 ')}`
            )
            reject(Upload.LIST_IGNORE)
          }
        }
      }
    } else {
      resolve(true)
    }
  })
}

const customRequest = async ({ file }) => {
  loading.value = true
  const api = `${process.env.VUE_APP_CMS}/cms/material/upload`
  const { success, retInfo, data } = await formRequest(api, { file })
  loading.value = false
  if (success) {
    const currentList = [...imageUrlList.value]
    currentList.push(data.url)
    emitChange(currentList)
    emit('update:materialId', '')
    emit('update:materialSource', '')
  } else {
    message.error(retInfo || '上传失败')
  }
}

const handleMaterialSubmit = data => {
  if (!data?.url) return
  let currentList = [...imageUrlList.value]
  if (props.limit === 1) {
    currentList = [data.url]
  } else {
    currentList.push(data.url)
  }
  emitChange(currentList)
  emit('update:materialId', data?.materialNo || '')
  emit('update:materialSource', data?.source || '')
}
</script>

<style lang="scss" scoped>
.uploadImg-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  .img-wrapper {
    position: relative;
    height: 104px;
    text-align: center;
    .img {
      max-width: 100%;
      height: 100%;
    }
    .hover-img {
      display: none;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
    }
    &:hover .hover-img {
      display: flex;
      background-color: rgba(0, 0, 0, 0.45);
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  }
}
.ant-upload-picture-card-wrapper {
  width: 104px;
  max-height: 104px;
  margin: 0;
  margin: 10px 10px 10px 0;
}
</style>
